import { Controller } from "@hotwired/stimulus"

// Persists a horizontally scrollable container's scroll position across Turbo
// frame swaps. The swap replaces the element (resetting scrollLeft to 0), so
// the position is saved to sessionStorage as the user scrolls and restored
// when the replacement element's controller connects.
//
// Containers whose contents change meaning between renders should vary the
// key (e.g. include the selected parent category) so unrelated content starts
// back at the left edge.
//
// A descendant marked with [data-preserve-scroll-reveal] (the active rail
// circle or tab) is scrolled into view after the position is restored: a
// selection made somewhere other than the rail itself (e.g. the all-filters
// modal) can activate an item that the remembered position leaves off-screen,
// which makes the filter look un-applied.
export default class extends Controller {
    static values = { key: String }

    initialize() {
        this.save = this.save.bind(this)
    }

    connect() {
        this.restore()
        this.element.addEventListener("scroll", this.save, { passive: true })
    }

    disconnect() {
        this.element.removeEventListener("scroll", this.save)

        if (this.restoreFrame) {
            cancelAnimationFrame(this.restoreFrame)
        }
    }

    save() {
        sessionStorage.setItem(this.storageKey, this.element.scrollLeft)
    }

    restore() {
        const stored = sessionStorage.getItem(this.storageKey)
        if (stored !== null) this.element.scrollLeft = Number(stored)

        // Re-apply after layout settles in case the first write landed before
        // the container had its full scrollable width, then make sure the
        // active item didn't end up off-screen.
        this.restoreFrame = requestAnimationFrame(() => {
            if (stored !== null) this.element.scrollLeft = Number(stored)
            this.revealActiveItem()
        })
    }

    revealActiveItem() {
        const item = this.element.querySelector("[data-preserve-scroll-reveal]")
        if (!item) return

        const containerRect = this.element.getBoundingClientRect()
        const itemRect = item.getBoundingClientRect()
        if (itemRect.left >= containerRect.left && itemRect.right <= containerRect.right) return

        const offset = (itemRect.left - containerRect.left) - (containerRect.width - itemRect.width) / 2
        this.element.scrollLeft = Math.max(0, this.element.scrollLeft + offset)
        this.save()
    }

    get storageKey() {
        return `preserve-scroll:${this.keyValue}:scroll-left`
    }
}
